import React from 'react';
import { Check, AlertCircle, RefreshCw, PauseCircle } from 'lucide-react';
import './StatusBadge.css';

/**
 * #3517: `paused` is a fourth state, not a shade of `error`. An operator parking a
 * mailbox to stop a runaway import must not produce a badge that tells the farmer their
 * integration is broken — during the 2026-08-17 incident that is plausibly what prompted
 * the next disconnect/reconnect cycle, which re-armed the import it was meant to stop.
 */
export type StatusBadgeVariant = 'connected' | 'error' | 'connecting' | 'paused';

export interface StatusBadgeProps {
  /** Status variant determining color and icon */
  variant: StatusBadgeVariant;
  /** Text to display next to the icon */
  text: string;
  /** Optional custom className */
  className?: string;
}

/**
 * StatusBadge - Displays a status indicator with icon and text.
 * Used to show connection status in integration cards.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  variant,
  text,
  className = '',
}) => {
  const getIcon = () => {
    switch (variant) {
      case 'connected':
        return <Check size={12} className="status-badge__icon" />;
      case 'error':
        return <AlertCircle size={12} className="status-badge__icon" />;
      case 'connecting':
        return <RefreshCw size={12} className="status-badge__icon status-badge__icon--spinning" />;
      case 'paused':
        return <PauseCircle size={12} className="status-badge__icon" />;
      default:
        return null;
    }
  };

  return (
    <span className={`status-badge status-badge--${variant} ${className}`}>
      {getIcon()}
      <span className="status-badge__text">{text}</span>
    </span>
  );
};

export default StatusBadge;
